import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import ROOT_URL from "../../../Utilities/apiconfig";

const disabledDoctors = [
  "Dr S Arivazhagan",
  "Dr. Munireddy M V",
  "Dr Ramanjanayalu",
  "Dr Anagha Auradkar",
  "Dr. Chandra Shekar M",
  "Dr Volga Joshy (PT)",
  "Dr Swetha",
  "Dr Mahesh",

];

const BookAppointmentsDoctors = () => {
  const { doctorId } = useParams();
  const navigate = useNavigate();

  const [doctor, setDoctor] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    patientName: "",
    phone: "",
    email: "",
    date: "",
    timeSlot: "",
    message: "",
    department: "",
  });

  const [timeSlots, setTimeSlots] = useState([]);
  const [bookedSlots, setBookedSlots] = useState([]);
  const [doctorBlockedDates, setDoctorBlockedDates] = useState([]); // 👈 NEW
  const [isDateBlocked, setIsDateBlocked] = useState(false);        // 👈 NEW

   const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isSunday, setIsSunday] = useState(false);

  const isSlotPassed = (slotStr) => {
    if (!formData.date) return false;
    const today = new Date().toISOString().split("T")[0];
    if (formData.date !== today) return false;
    const match = slotStr.match(/(\d{1,2}):(\d{2})\s*(AM|PM)/i);
    if (!match) return false;
    let hours = parseInt(match[1], 10);
    const minutes = parseInt(match[2], 10);
    const period = match[3].toUpperCase();
    if (period === "PM" && hours !== 12) hours += 12;
    if (period === "AM" && hours === 12) hours = 0;
    const now = new Date();
    const slotMinutes = hours * 60 + minutes;
    const nowMinutes = now.getHours() * 60 + now.getMinutes();
    return slotMinutes <= nowMinutes;
  };

  // Fetch doctor details (including timeSlots + blockedDates)
  useEffect(() => {
    const fetchDoctor = async () => {
      try {
        const response = await axios.get(`${ROOT_URL}/api/medicos/${doctorId}`);
        const data = response.data;

        setDoctor(data);
        setTimeSlots(data.timeSlots || []);
        setDoctorBlockedDates(data.blockedDates || []); // 👈 NEW

        setFormData((prevData) => ({
          ...prevData,
          department: data.department || "",
        }));
      } catch (err) {
        console.error(err);
        setError("Failed to fetch doctor details");
      } finally {
        setLoading(false);
      }
    };

    fetchDoctor();
  }, [doctorId]);

  // Check if selected date is blocked
  useEffect(() => {
    if (formData.date && doctorBlockedDates.length > 0) {
      const blocked = doctorBlockedDates.includes(formData.date);
      setIsDateBlocked(blocked);

      if (blocked) {
        // Clear selected timeSlot and bookedSlots, but keep timeSlots intact
        setBookedSlots([]);
        setFormData((prev) => ({ ...prev, timeSlot: "" }));
      }
    } else {
      setIsDateBlocked(false);
    }
  }, [formData.date, doctorBlockedDates]);

  // Fetch booked slots for this doctor + selected date (only if date not blocked)
  useEffect(() => {
    const fetchBookedSlots = async () => {
      if (doctor && formData.date && !isDateBlocked) {
        try {
          const response = await axios.get(`${ROOT_URL}/api/appointments/all`);
          const appointments = response.data.filter(
            (appt) =>
              appt.doctorName === doctor.name && appt.date === formData.date
          );
          setBookedSlots(appointments.flatMap((appt) => appt.timeSlots || []));
        } catch (err) {
          console.error("Failed to fetch booked slots:", err);
        }
      } else {
        setBookedSlots([]);
      }
    };

    fetchBookedSlots();
  }, [doctor, formData.date, isDateBlocked]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleDateChange = (e) => {
    const selectedDate = e.target.value;
    const day = new Date(selectedDate).getDay();
    if (day === 0) {
      setIsSunday(true);
      setFormData({ ...formData, date: "" });
    } else {
      setIsSunday(false);
      setFormData({ ...formData, [e.target.name]: e.target.value });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!doctor) return;

    if (isDoctorDisabled) {
      toast.error("No doctor slots available.");
      return;
    }

    if (isSunday) {
      toast.error("Appointments are not available on Sundays. Please select another date.");
      return;
    }

    if (isDateBlocked) {
      toast.error("No doctor slots available on the selected date.");
      return;
    }

    if (formData.timeSlot && isSlotPassed(formData.timeSlot)) {
      toast.error("Slot not available. Please select another time slot.");
      return;
    }

    // Validate mobile number
    if (formData.phone.length < 10) {
      toast.error("Mobile number should be at least 10 digits.");
      return;
    }

    setIsSubmitting(true);

    try {
      await axios.post(`${ROOT_URL}/api/appointments/add`, {
        patientName: formData.patientName,
        phone: formData.phone,
        email: formData.email,
        date: formData.date,
        timeSlots: [formData.timeSlot],
        message: formData.message,
        doctorName: doctor.name,
        departmentName: doctor.department,
      });

      toast.success(
        "We’ve received your request. You will receive a call shortly to confirm your appointment."
      );

      setTimeout(() => {
        navigate("/doctors");
      }, 10000);
    } catch (error) {
      console.error("Error booking appointment:", error);
      toast.error("Failed to book appointment. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading) return <p>Loading...</p>;
  if (error) return <p>{error}</p>;

  const isDoctorDisabled = disabledDoctors.includes(doctor.name);

  return (
    <div className="container mt-4">
      <h2 className="mb-4">
        Booking Appointment for{" "}
        <span className="text-primary">{doctor.name}</span>
      </h2>
      {isDoctorDisabled && (
        <div className="alert alert-warning" role="alert">
          No doctor slots available. Please select a different doctor.
        </div>
      )}
      <form onSubmit={handleSubmit} className="border p-5 rounded shadow">
        <div className="mb-3">
          <label className="form-label">Patient Name</label>
          <input
            type="text"
            className="form-control"
            name="patientName"
            value={formData.patientName}
            onChange={handleChange}
            required
          />
        </div>

        <div className="mb-3">
          <label className="form-label">Mobile Number</label>
          <input
            type="number"
            className="form-control"
            name="phone"
            value={formData.phone}
            onChange={handleChange}
            required
          />
        </div>

        <div className="mb-3">
          <label className="form-label">Email</label>
          <input
            type="email"
            className="form-control"
            name="email"
            value={formData.email}
            onChange={handleChange}
            required
          />
        </div>

        <div className="mb-3">
          <label className="form-label">Department</label>
          <input
            type="text"
            className="form-control"
            name="department"
            value={formData.department}
            disabled
          />
        </div>

        <div className="mb-3">
          <label className="form-label">Date</label>
          <input
            type="date"
            className="form-control"
            name="date"
            value={formData.date}
            onChange={handleDateChange}
             required
             min={new Date().toISOString().split("T")[0]}
           />
           {isDateBlocked && (
             <p className="text-danger mt-1">
               No slots available on this date. Please choose another date.
             </p>
           )}
           {isSunday && (
             <p className="text-danger mt-1">
               Appointments are not available on Sundays. Please select another date.
             </p>
           )}
        </div>

        <div className="mb-3">
          <label className="form-label">Time Slot</label>
          <select
            className="form-control"
            name="timeSlot"
            value={formData.timeSlot}
            onChange={handleChange}
            required
            disabled={isDateBlocked || timeSlots.length === 0}
          >
            <option value="">
              {isDateBlocked
                ? "No doctor slots available"
                : "Select Time Slot"}
            </option>
            {!isDateBlocked &&
              timeSlots
                .flatMap((slot) => slot.split(","))
                .map((slot, index) => {
                  const trimmed = slot.trim();
                  const passed = isSlotPassed(trimmed);
                  const isBooked = bookedSlots.includes(trimmed);
                  return (
                    <option
                      key={index}
                      value={trimmed}
                      disabled={isBooked || passed}
                    >
                      {trimmed} {isBooked ? "(Booked)" : passed ? "(Slot Not Available)" : ""}
                    </option>
                  );
                })}
          </select>
        </div>

        <div className="mb-3">
          <label className="form-label">Message</label>
          <textarea
            className="form-control"
            name="message"
            value={formData.message}
            onChange={handleChange}
          />
        </div>

        <button
          type="submit"
          className="btn btn-primary"
          disabled={isSubmitting || isDoctorDisabled}
        >
          {isSubmitting ? "Booking . . ." : "Book Appointment"}
        </button>
      </form>

      <ToastContainer
        position="top-right"
        autoClose={20000}
        hideProgressBar={false}
        closeOnClick
        pauseOnHover
      />
    </div>
  );
};

export default BookAppointmentsDoctors;
